/**
 * River surface and shoreline foam geometry (GDD §3.4; concept L704–L724).
 */
import { BufferAttribute, BufferGeometry, Color } from 'three';
import { lerp, sstep } from '../core/math.ts';
import { buildRibbon } from './geometry.ts';
import type { Look } from './looks.ts';
import type { Mode } from './modes.ts';
import type { WorldProfile } from './profiles.ts';

/** The world strip spans z from WORLD_Z0 down to worldZ1 (concept L704). */
export const WORLD_Z0 = 160;
export const worldZ1 = (mode: Mode) => (mode.loop ? -1500 : -900);

/** Metres per texture repeat along the river: water normal maps (x and z) and bank foam (z). */
export const WATER_UV = 26;
export const FOAM_UV = 34;
/** The clearcoat normal map's extra repeat over the water UVs (concept L716). */
export const N2_REPEAT = 1.7;

/** Water surface: 14 columns reaching 9 m past each bank, tinted deep→shallow toward the banks (GDD §3.4). */
export function waterGeometry(
  W: WorldProfile,
  look: Look,
  z0: number,
  z1: number,
  nz = Math.round((z0 - z1) / 5),
): BufferGeometry {
  const deep = new Color(look.deep);
  const shallow = new Color(look.shallow);
  return buildRibbon(W, z0, z1, nz, 14, (u, c, w, z, out) => {
    const off = (u * 2 - 1) * (w + 9);
    const x = c + off;
    out.copy(deep).lerp(shallow, sstep(0.35, 1.0, Math.abs(off) / w));
    return [x, 0, x / WATER_UV, z / WATER_UV];
  });
}

/** Foam band along one bank (side −1 left, +1 right), 2.4 m onto land to 3.2 m into the water. */
export function foamGeometry(
  W: WorldProfile,
  side: -1 | 1,
  z0: number,
  z1: number,
  nz = Math.round((z0 - z1) / 3),
): BufferGeometry {
  return buildRibbon(W, z0, z1, nz, 3, (u, c, w, z, out) => {
    out.setRGB(1, 1, 1);
    const dl = lerp(2.4, -3.2, u);
    return [c + side * (w + dl), 0.06, z / FOAM_UV, u];
  });
}

/**
 * Clock-driven texture offsets (concept L1452). A texture offset increasing along v moves features toward −z,
 * which is upriver (the jet flies −z); GDD §3.4 (D3) wants the normal maps to scroll downstream, so the GDD
 * setting reverses the along-river component at the same speeds. The foam's motion is the concept's (GDD silent).
 */
export function waterOffsets(t: number, scroll: 'upriver' | 'downstream') {
  const s = scroll === 'downstream' ? -1 : 1;
  return {
    n1: [t * 0.006, s * t * 0.035] as [number, number],
    n2: [-t * 0.011, s * t * 0.052] as [number, number],
    foam: [t * 0.05, 0] as [number, number],
  };
}

/**
 * The island's waterline: the island height −0.6 + 6.8·(1−e)^0.55 (concept L58) crosses the water surface at
 * e = 1 − (0.6/6.8)^(1/0.55) ≈ 0.988, i.e. at this fraction of the island ellipse's radii.
 */
export const ISLAND_SHORE = Math.sqrt(1 - Math.pow(0.6 / 6.8, 1 / 0.55));

/** A point on the island ellipse around `islandZ` at angle θ and radius factor s (concept L56: rx = 0.36·w, rz = 44). */
export function islandPoint(W: WorldProfile, islandZ: number, theta: number, s: number): [number, number] {
  const z = islandZ + 44 * s * Math.sin(theta);
  return [W.center(z) + 0.36 * W.width(z) * s * Math.cos(theta), z];
}

/**
 * Foam ring around an island (GDD §3.4 "every shoreline", D2): the same band as the banks, from 2.4 m onto the
 * island to 3.2 m into the water, measured along the shoreline's outward normal.
 */
export function islandFoamGeometry(W: WorldProfile, islandZ: number, segments = 180): BufferGeometry {
  const ring: [number, number][] = [];
  for (let i = 0; i <= segments; i++) ring.push(islandPoint(W, islandZ, (i / segments) * Math.PI * 2, ISLAND_SHORE));
  let length = 0;
  const along = [0];
  for (let i = 1; i <= segments; i++) {
    const [ax, az] = ring[i - 1] ?? [0, 0];
    const [bx, bz] = ring[i] ?? [0, 0];
    length += Math.hypot(bx - ax, bz - az);
    along.push(length);
  }
  const uScale = Math.max(1, Math.round(length / 34)) / length; // whole repeats so the seam is invisible
  const cols = 3;
  const pos = new Float32Array((segments + 1) * (cols + 1) * 3);
  const uv = new Float32Array((segments + 1) * (cols + 1) * 2);
  const [cx, cz] = islandPoint(W, islandZ, 0, 0);
  let v = 0;
  for (let i = 0; i <= segments; i++) {
    const [px, pz] = ring[i] ?? [0, 0];
    const [ax, az] = ring[Math.max(0, i - 1)] ?? [px, pz];
    const [bx, bz] = ring[Math.min(segments, i + 1)] ?? [px, pz];
    let nx = bz - az;
    let nz = -(bx - ax);
    const l = Math.hypot(nx, nz) || 1;
    nx /= l;
    nz /= l;
    if (nx * (px - cx) + nz * (pz - cz) < 0) {
      nx = -nx;
      nz = -nz;
    }
    for (let c = 0; c <= cols; c++) {
      const u = c / cols;
      const d = lerp(-2.4, 3.2, u);
      pos.set([px + nx * d, 0.06, pz + nz * d], v * 3);
      uv.set([(along[i] ?? 0) * uScale, u], v * 2);
      v++;
    }
  }
  const idx: number[] = [];
  for (let i = 0; i < segments; i++)
    for (let c = 0; c < cols; c++) {
      const a = i * (cols + 1) + c;
      const b = a + 1;
      const cc = a + cols + 1;
      const dd = cc + 1;
      idx.push(a, b, cc, b, dd, cc);
    }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(pos, 3));
  geo.setAttribute('uv', new BufferAttribute(uv, 2));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  return geo;
}
